import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { ADMIN_COLORS, PROMO_RATE_MODES, getPromoRateModeOptions } from '../constants/bookingConst';
import { WEEKDAYS, getAllConfiguredTimeSlots } from '../utils/bookingUtils';
import { describePromoRate } from '../utils/promoUtils';
import { getCurrencySymbol } from '../utils/currencyUtil';
import '../css/AdminPromo.css';

const COLORS = ADMIN_COLORS;

const newId = (prefix) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

/** A rule that constrains nothing: the whole promo at one price. */
const blankRule = () => ({
  id: newId('rule'),
  courts: [],
  days: [],
  timeSlots: [],
  mode: PROMO_RATE_MODES.PERCENT,
  value: '',
});

const blankPromo = () => ({
  id: newId('promo'),
  name: '',
  description: '',
  startDate: '',
  endDate: '',
  sportId: '',
  enabled: true,
  rules: [blankRule()],
});

/**
 * Create / edit one promo.
 *
 * Every list on a rule — courts, days, hours — is EMPTY by default and empty
 * means "all of them". That is what keeps the common promo ("20% off
 * everything next month") a matter of typing a name, two dates and a number,
 * while still allowing "₱200/hr, court 3, Tuesday mornings" from the same
 * form.
 *
 * @param {object} props
 * @param {object|null} props.promo - null to create
 * @param {object} props.config
 * @param {boolean} props.saving
 * @param {(promo: object) => void} props.onSave
 * @param {() => void} props.onClose
 * @returns {JSX.Element}
 */
function AdminPromoModal({ promo, config, saving, onSave, onClose }) {
  const [draft, setDraft] = useState(() => (promo ? {
    ...blankPromo(),
    ...promo,
    rules: (Array.isArray(promo.rules) && promo.rules.length > 0)
      ? promo.rules.map(rule => ({ ...blankRule(), ...rule }))
      : [blankRule()],
  } : blankPromo()));
  const [error, setError] = useState('');

  const sports = config?.sports || [];
  // Courts narrow to the promo's sport once one is chosen — offering a Tennis
  // court under a Pickleball promo would build a rule that can never match.
  const courtOptions = useMemo(() => (
    (config?.courts || [])
      .filter(c => !draft.sportId || c.sportId === draft.sportId)
      .map(c => ({ number: Number(c.number), label: c.name || `Court ${c.number}` }))
      .filter(c => Number.isFinite(c.number))
      .sort((a, b) => a.number - b.number)
  ), [config?.courts, draft.sportId]);
  // Every hour configured anywhere, not one day's opening hours: a promo can
  // cover a weekday hour the club does not open on weekends.
  const slotOptions = useMemo(() => getAllConfiguredTimeSlots(config), [config]);

  const patch = (fields) => setDraft(prev => ({ ...prev, ...fields }));

  const patchRule = (ruleId, fields) => setDraft(prev => ({
    ...prev,
    rules: prev.rules.map(rule => (rule.id === ruleId ? { ...rule, ...fields } : rule)),
  }));

  /**
   * Adds or removes one value in a rule's list dimension.
   * @param {string} ruleId
   * @param {'courts'|'days'|'timeSlots'} key
   * @param {number|string} value
   */
  const toggleIn = (ruleId, key, value) => setDraft(prev => ({
    ...prev,
    rules: prev.rules.map((rule) => {
      if (rule.id !== ruleId) return rule;
      const current = Array.isArray(rule[key]) ? rule[key] : [];
      const has = current.includes(value);
      return { ...rule, [key]: has ? current.filter(v => v !== value) : [...current, value] };
    }),
  }));

  const addRule = () => setDraft(prev => ({ ...prev, rules: [...prev.rules, blankRule()] }));
  const removeRule = (ruleId) => setDraft(prev => ({
    ...prev,
    rules: prev.rules.filter(rule => rule.id !== ruleId),
  }));

  const handleSave = () => {
    if (!draft.name.trim()) { setError('Give this promo a name.'); return; }
    if (draft.startDate && draft.endDate && draft.endDate < draft.startDate) {
      setError('The end date is before the start date.');
      return;
    }
    const rules = draft.rules.filter(rule => String(rule.value).trim() !== '');
    if (rules.length === 0) { setError('Add at least one rate — a promo with none changes nothing.'); return; }
    const bad = rules.find(rule => !Number.isFinite(Number(rule.value)) || Number(rule.value) < 0);
    if (bad) { setError('Every rate needs a number, 0 or more.'); return; }
    if (rules.some(rule => rule.mode === PROMO_RATE_MODES.PERCENT && Number(rule.value) > 100)) {
      setError('A percentage discount cannot be more than 100%.');
      return;
    }

    setError('');
    onSave({
      ...draft,
      name: draft.name.trim(),
      description: draft.description.trim(),
      // Numbers, not the strings the inputs hold, so nothing downstream has
      // to coerce them before doing arithmetic.
      rules: rules.map(rule => ({ ...rule, value: Number(rule.value) })),
    });
  };

  return createPortal(
    // Backdrop deliberately has no onClick: this form holds unsaved work, and
    // a stray tap outside it must not throw that away (CLAUDE.md > Behavior).
    <div className="promo-modal-overlay">
      <div className="promo-modal" role="dialog" aria-modal="true" aria-label={promo ? 'Edit promo' : 'Create promo'}>
        <header className="promo-modal-head">
          <h3>{promo ? 'Edit promo' : 'Create promo'}</h3>
          <button type="button" className="promo-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="promo-modal-body">
          <label className="promo-field">
            <span>Promo name</span>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => patch({ name: e.target.value })}
              placeholder="e.g. Opening Week"
            />
          </label>

          <label className="promo-field">
            <span>Description</span>
            <textarea
              rows={2}
              value={draft.description}
              onChange={(e) => patch({ description: e.target.value })}
              placeholder="What this promo is, in a line. Shown on the card only."
            />
          </label>

          <div className="promo-field-row">
            <label className="promo-field">
              <span>Starts</span>
              <input type="date" value={draft.startDate} onChange={(e) => patch({ startDate: e.target.value })} />
            </label>
            <label className="promo-field">
              <span>Ends</span>
              <input type="date" value={draft.endDate} onChange={(e) => patch({ endDate: e.target.value })} />
            </label>
          </div>
          <p className="promo-hint">Leave a date blank for an open-ended run. Both ends are included.</p>

          <label className="promo-field">
            <span>Sport</span>
            <select
              value={draft.sportId}
              onChange={(e) => patch({
                sportId: e.target.value,
                // Court picks belong to the old sport and would never match
                // again, so they are cleared rather than left as dead config.
                rules: draft.rules.map(rule => ({ ...rule, courts: [] })),
              })}
            >
              <option value="">All sports</option>
              {sports.map(sport => <option key={sport.id} value={sport.id}>{sport.name}</option>)}
            </select>
          </label>

          <div className="promo-rules">
            <div className="promo-rules-head">
              <h4>Rates</h4>
              <p className="promo-hint">
                Each rate applies to the courts, days and hours you tick. Tick nothing on a row and it
                covers all of them. Where two rates overlap, the guest gets the cheaper one.
              </p>
            </div>

            {draft.rules.map((rule, index) => (
              <div className="promo-rule" key={rule.id}>
                <div className="promo-rule-head">
                  <strong>Rate {index + 1}</strong>
                  <span className="promo-rule-preview">{describePromoRate({ ...rule, value: Number(rule.value) }, config)}</span>
                  {draft.rules.length > 1 && (
                    <button type="button" className="promo-rule-remove" onClick={() => removeRule(rule.id)}>
                      Remove
                    </button>
                  )}
                </div>

                <div className="promo-rule-price">
                  <select value={rule.mode} onChange={(e) => patchRule(rule.id, { mode: e.target.value })}>
                    {getPromoRateModeOptions(getCurrencySymbol(config)).map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    value={rule.value}
                    onChange={(e) => patchRule(rule.id, { value: e.target.value })}
                    placeholder={rule.mode === PROMO_RATE_MODES.PERCENT ? '20' : '250'}
                  />
                </div>

                <PickerRow label="Courts" empty="All courts">
                  {courtOptions.map(court => (
                    <Chip
                      key={court.number}
                      on={(rule.courts || []).includes(court.number)}
                      onClick={() => toggleIn(rule.id, 'courts', court.number)}
                    >
                      {court.label}
                    </Chip>
                  ))}
                </PickerRow>

                <PickerRow label="Days" empty="Every day">
                  {WEEKDAYS.map(({ index: day, short, label }) => (
                    <Chip
                      key={day}
                      title={label}
                      on={(rule.days || []).includes(day)}
                      onClick={() => toggleIn(rule.id, 'days', day)}
                    >
                      {short}
                    </Chip>
                  ))}
                </PickerRow>

                <PickerRow label="Hours" empty="Every hour">
                  {slotOptions.map(slot => (
                    <Chip
                      key={slot}
                      on={(rule.timeSlots || []).includes(slot)}
                      onClick={() => toggleIn(rule.id, 'timeSlots', slot)}
                    >
                      {slot}
                    </Chip>
                  ))}
                </PickerRow>
              </div>
            ))}

            <button type="button" className="promo-add-rule" onClick={addRule}>+ Add another rate</button>
          </div>

          {error && <p className="promo-error">{error}</p>}
        </div>

        <footer className="promo-modal-foot">
          <button type="button" className="promo-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="promo-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (promo ? 'Save changes' : 'Create promo')}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/**
 * One labelled row of chips, with the "nothing ticked" meaning spelled out
 * beside it — otherwise an untouched row reads as "no courts" rather than
 * "all courts", which is the opposite of what it does.
 */
const PickerRow = ({ label, empty, children }) => (
  <div className="promo-picker">
    <span className="promo-picker-label">{label}</span>
    <div className="promo-picker-chips">{children}</div>
    <span className="promo-picker-empty">{empty} if none ticked</span>
  </div>
);

const Chip = ({ on, onClick, title, children }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={`promo-chip${on ? ' on' : ''}`}
    style={on ? { background: COLORS.cyanDeep, borderColor: COLORS.cyanDeep, color: '#fff' } : undefined}
  >
    {children}
  </button>
);

export default AdminPromoModal;
